import { useCallback } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { publicEnvConfig } from 'config/public';
import type {
	DeleteContact,
	DeleteWishlist,
	DeleteWishlistVariant,
	GetBundleVariants,
	GetCampaignProducts,
	GetCart,
	GetCartMini,
	GetContact,
	GetContacts,
	GetCreditSimulation,
	GetCustomer,
	GetCustomerAvailability,
	GetCustomerBonus,
	GetCustomerBonusChecks,
	GetCustomerInvoices,
	GetCustomerOrder,
	GetCustomerOrders,
	GetCustomerOrderStatus,
	GetCustomerSearch,
	GetDeliveryMethods,
	GetFaqSearch,
	GetJulaProSearchCompany,
	GetJulaProWorksite,
	GetNearbyStores,
	GetOrder,
	GetPermissions,
	GetProductCustomization,
	GetProductQuestions,
	GetQuotation,
	GetQuotations,
	GetRelatedProducts,
	GetResursPaymentPriceSignage,
	GetSearchPopularProducts,
	GetSearchPopularTerms,
	GetSharedWishlist,
	GetSimilarProducts,
	GetStore,
	GetStores,
	GetTicket,
	GetUserFileStorage,
	GetVariant,
	GetVariantStock,
	GetVariantStockStores,
	GetWishlistMini,
	PostCart,
	PostCartFinalizePayment,
	PostCartMiniVariant,
	PostCartMiniVariants,
	PostCartPayment,
	PostCartVariant,
	PostCartVariants,
	PostCheckoutCart,
	PostConsent,
	PostCopyCart,
	PostFeedback,
	PostProductCustomization,
	PostProductList,
	PostQuotationPayment,
	PostSearch,
	PostSearchAdvanced,
	PostSearchProductArchive,
	PostShareCart,
	PostShareWishlist,
	PostTrackEvent,
	PostUserFileStorage,
	PostWishlist,
	PostWishlistMiniVariant,
	PostWishlistMiniVariants,
	PostWishlistVariant,
	PostWishlistVariants,
	PutCart,
	PutContactAllowOnlinePurchase,
	PutCustomerOrderStatus,
	PutWishlistVariantPicked,
	VerifyRedirectPayment,
} from 'models/api';
import type { Branded, DistributiveOmit } from 'types';
import { mapValues } from 'utils/collection';
import {
	fetchData,
	type FetchError,
	fetchResult,
	type RequestOptions,
} from 'utils/fetch';
import { formatTemplate } from 'utils/format';
import { ignorePromiseRejection, is } from 'utils/helpers';
import { createUrl, joinPath } from 'utils/url';

export const TEST_API_URL = 'https://api.test/v1/';
export const API_URL: string =
	(process.env.NODE_ENV === 'test'
		? TEST_API_URL
		: publicEnvConfig.DIGITAL_PLATFORM_API_URL_SEGMENT) ?? '/';

type Endpoint =
	| DeleteContact
	| DeleteWishlist
	| DeleteWishlistVariant
	| GetBundleVariants
	| GetCampaignProducts
	| GetCart
	| GetCartMini
	| GetContact
	| GetContacts
	| GetCreditSimulation
	| GetCustomer
	| GetCustomerAvailability
	| GetCustomerBonus
	| GetCustomerBonusChecks
	| GetCustomerInvoices
	| GetCustomerOrder
	| GetCustomerOrders
	| GetCustomerOrderStatus
	| GetCustomerSearch
	| GetDeliveryMethods
	| GetFaqSearch
	| GetJulaProSearchCompany
	| GetJulaProWorksite
	| GetNearbyStores
	| GetOrder
	| GetPermissions
	| GetProductCustomization
	| GetProductQuestions
	| GetQuotation
	| GetQuotations
	| GetRelatedProducts
	| GetResursPaymentPriceSignage
	| GetSearchPopularProducts
	| GetSearchPopularTerms
	| GetSharedWishlist
	| GetSimilarProducts
	| GetStore
	| GetStores
	| GetTicket
	| GetUserFileStorage
	| GetVariant
	| GetVariantStock
	| GetVariantStockStores
	| GetWishlistMini
	| PostCart
	| PostCartFinalizePayment
	| PostCartMiniVariant
	| PostCartMiniVariants
	| PostCartPayment
	| PostCartVariant
	| PostCartVariants
	| PostCheckoutCart
	| PostConsent
	| PostCopyCart
	| PostFeedback
	| PostProductCustomization
	| PostProductList
	| PostQuotationPayment
	| PostSearch
	| PostSearchAdvanced
	| PostSearchProductArchive
	| PostShareCart
	| PostShareWishlist
	| PostTrackEvent
	| PostUserFileStorage
	| PostWishlist
	| PostWishlistMiniVariant
	| PostWishlistMiniVariants
	| PostWishlistVariant
	| PostWishlistVariants
	| PutCart
	| PutContactAllowOnlinePurchase
	| PutCustomerOrderStatus
	| PutWishlistVariantPicked
	| VerifyRedirectPayment;

type EndpointsPathMethod = { [T in Endpoint as T['name']]: T['pathMethod'] };

// prettier-ignore
export const endpoints: EndpointsPathMethod = {
	DeleteContact: { method: 'DELETE', path: 'Customer/julapro/contact/{{customerContactId}}' },
	DeleteWishlist: { method: 'DELETE', path: 'Wishlist' },
	DeleteWishlistVariant: { method: 'DELETE', path: 'Wishlist/variants/{{variantId}}' },
	GetBundleVariants: { method: 'GET', path: 'Bundle/{{bundleId}}/variants' },
	GetCampaignProducts: { method: 'GET', path: 'Campaign/{{campaignId}}' },
	GetCart: { method: 'GET', path: 'Cart/{{cartId}}' },
	GetCartMini: { method: 'GET', path: 'Cart/mini' },
	GetContact: { method: 'GET', path: 'Customer/julapro/contact/{{customerContactId}}' },
	GetContacts: { method: 'GET', path: 'Customer/julapro/contact' },
	GetCreditSimulation: { method: 'GET', path: 'Customer/CreditSimulation' },
	GetCustomer: { method: 'GET', path: 'Customer' },
	GetCustomerAvailability: { method: 'GET', path: 'CustomerService/availability' },
	GetCustomerBonus: { method: 'GET', path: 'Customer/bonus' },
	GetCustomerBonusChecks: { method: 'GET', path: 'Customer/bonusChecks' },
	GetCustomerInvoices: { method: 'GET', path: 'Customer/invoice' },
	GetCustomerOrder: { method: 'GET', path: 'Customer/order/{{orderId}}' },
	GetCustomerOrders: { method: 'GET', path: 'Customer/order' },
	GetCustomerOrderStatus: { method: 'GET', path: 'Customer/{{customerId}}/order/{{orderId}}' },
	GetCustomerSearch: { method: 'GET', path: 'Customer/search' },
	GetDeliveryMethods: { method: 'GET', path: 'DeliveryMethods/variant/{{variantId}}' },
	GetFaqSearch: { method: 'GET', path: 'FaqSearch/{{searchString}}' },
	GetJulaProSearchCompany: { method: 'GET', path: 'Customer/julapro/search/company' },
	GetJulaProWorksite: { method: 'GET', path: 'Customer/julapro/worksite/{{worksiteId}}' },
	GetNearbyStores: { method: 'GET', path: 'Stores/nearbyStores' },
	GetOrder: { method: 'GET', path: 'Customer/order' },
	GetPermissions: { method: 'GET', path: 'Customer/permissions' },
	GetProductCustomization: { method: 'GET', path: 'ProductCustomization/{{variantId}}' },
	GetProductQuestions: { method: 'GET', path: 'ProductQuestions/{{variantId}}' },
	GetQuotation: { method: 'GET', path: 'Quotations/{{quotationId}}' },
	GetQuotations: { method: 'GET', path: 'Quotations' },
	GetRelatedProducts: { method: 'GET', path: 'variant/{{variantId}}/relatedproducts' },
	GetResursPaymentPriceSignage: { method: 'GET', path: 'payment/resursbank/price-signage' },
	GetSearchPopularProducts: { method: 'GET', path: 'Search/popularproducts' },
	GetSearchPopularTerms: { method: 'GET', path: 'Search/popularterms' },
	GetSharedWishlist: { method: 'GET', path: 'Wishlist/{{wishlistId}}' },
	GetSimilarProducts: { method: 'GET', path: 'Product/{{productId}}/similar' },
	GetStore: { method: 'GET', path: 'Stores/{{storeId}}' },
	GetStores: { method: 'GET', path: 'Stores' },
	GetTicket: { method: 'GET', path: 'Ticket/{{ticketType}}' },
	GetUserFileStorage: { method: 'GET', path: 'UserFileStorage/{{storageType}}' },
	GetVariant: { method: 'GET', path: 'variant/{{variantId}}' },
	GetVariantStock: { method: 'GET', path: 'variantStock' },
	GetVariantStockStores: { method: 'GET', path: 'variantStock/{{variantId}}/stores' },
	GetWishlistMini: { method: 'GET', path: 'Wishlist/mini' },
	PostCart: { method: 'POST', path: 'Cart' },
	PostCartFinalizePayment: { method: 'POST', path: 'Cart/{{cartId}}/finalize' },
	PostCartMiniVariant: { method: 'POST', path: 'Cart/mini/variants/{{variantId}}' },
	PostCartMiniVariants: { method: 'POST', path: 'Cart/mini/variants' },
	PostCartPayment: { method: 'POST', path: 'Cart/{{cartId}}/payment' },
	PostCartVariant: { method: 'POST', path: 'Cart/variants/{{variantId}}' },
	PostCartVariants: { method: 'POST', path: 'Cart/variants' },
	PostConsent: { method: 'POST', path: 'Customer/julaclub/consent' },
	PostCheckoutCart: { method: 'POST', path: 'Cart/checkout' },
	PostCopyCart: { method: 'POST', path: 'Cart/{{cartId}}/copy' },
	PostFeedback: { method: 'POST', path: 'Feedback' },
	PostProductCustomization: { method: 'POST', path: 'ProductCustomization/{{variantId}}' },
	PostProductList: { method: 'POST', path: 'ProductList' },
	PostQuotationPayment: { method: 'POST', path: 'Quotations/{{quotationId}}/payment' },
	PostSearch: { method: 'POST', path: 'Search' },
	PostSearchAdvanced: { method: 'POST', path: 'Search/advanced' },
	PostSearchProductArchive: { method: 'POST', path: 'Search/productarchive' },
	PostShareCart: { method: 'POST', path: 'Wishlist/sharecart/{{cartId}}' },
	PostShareWishlist: { method: 'POST', path: 'Wishlist/share' },
	PostTrackEvent: { method: 'POST', path: 'TrackEvent' },
	PostUserFileStorage: { method: 'POST', path: 'UserFileStorage/{{storageType}}' },
	PostWishlist: { method: 'POST', path: 'Wishlist' },
	PostWishlistMiniVariant: { method: 'POST', path: 'Wishlist/mini/variants/{{variantId}}' },
	PostWishlistMiniVariants: { method: 'POST', path: 'Wishlist/mini/variants' },
	PostWishlistVariant: { method: 'POST', path: 'Wishlist/variants/{{variantId}}' },
	PostWishlistVariants: { method: 'POST', path: 'Wishlist/variants' },
	PutCart: { method: 'PUT', path: 'Cart/{{cartId}}' },
	PutContactAllowOnlinePurchase: { method: 'PUT', path: 'Customer/julapro/contact/{{customerContactId}}' },
	PutCustomerOrderStatus: { method: 'PUT', path: 'Customer/{{customerId}}/order/{{orderId}}' },
	PutWishlistVariantPicked: { method: 'PUT', path: 'Wishlist/variants/{{variantId}}/pick' },
	VerifyRedirectPayment: { method: 'POST', path: 'Cart/{{cartId}}/verifyredirectpayment' },
};

function buildUrl(
	path: string,
	pathParams: Record<string, string> | undefined,
	queryParams:
		| Record<string, string | number | (string | number)[] | undefined | null>
		| undefined,
) {
	return createUrl(
		joinPath(
			API_URL,
			formatTemplate(
				path,
				mapValues(pathParams ?? {}, (v) => encodeURIComponent(v)),
			),
		),
		queryParams ?? {},
	);
}

type EndpointWithParams = Extract<Endpoint, { params: unknown }>;
type EndpointWithoutParams = Exclude<Endpoint, EndpointWithParams>;

type FlattenedEndpointInput<E extends EndpointWithParams> = {
	name: E['name'];
} & E['params'];

type EndpointWithParamsInput = {
	[E in EndpointWithParams as E['name']]: FlattenedEndpointInput<E>;
}[EndpointWithParams['name']];

type EndpointWithParamsUrlInput = DistributiveOmit<
	EndpointWithParamsInput,
	'body'
>;

type EndpointNameWithoutParamsInput = EndpointWithoutParams['name'];

type EndpointInput = EndpointWithParamsInput | EndpointNameWithoutParamsInput;

type EndpointUrlInput =
	| EndpointWithParamsUrlInput
	| EndpointNameWithoutParamsInput;

type EndpointName = Endpoint['name'];

export type EndpointBody<Name extends EndpointName> =
	Name extends EndpointWithParams['name']
		? ExtractEndpoint<Name> extends { params: { body: infer B } }
			? B
			: never
		: never;

/** Get an API endpoint URL. */
export function getEndpointUrl(input: EndpointUrlInput) {
	const hasParams = is.object(input);
	const pathParams =
		hasParams && 'pathParams' in input ? input.pathParams : undefined;
	const queryParams =
		hasParams && 'queryParams' in input ? input.queryParams : undefined;

	const name = is.string(input) ? input : input.name;
	return buildUrl(endpoints[name].path, pathParams, queryParams);
}

function getEndpointFetchOptions(input: EndpointInput) {
	const body =
		is.object(input) && 'body' in input
			? JSON.stringify(input.body)
			: undefined;
	const name = is.string(input) ? input : input.name;
	return {
		method: endpoints[name].method,
		body,
	} satisfies RequestOptions;
}

type ExtractEndpoint<Name extends EndpointName> = Extract<
	Endpoint,
	{ name: Name }
>;

type EndpointResponse<Name extends EndpointName> =
	ExtractEndpoint<Name> extends { response: infer R } ? R : never;

type EndpointInputName<T extends EndpointInput> = T extends { name: infer N }
	? N
	: T;

/** Do a request to an API endpoint and get the parsed response. */
export function fetchEndpointData<T extends EndpointInput>(input: T) {
	return fetchData<EndpointResponse<EndpointInputName<T>>>(
		getEndpointUrl(input),
		getEndpointFetchOptions(input),
	);
}

/** Do a request to an API endpoint and get a result type. */
export function fetchEndpointResult<T extends EndpointInput>(input: T) {
	return fetchResult<EndpointResponse<EndpointInputName<T>>>(
		getEndpointUrl(input),
		getEndpointFetchOptions(input),
	);
}

type EndpointKey = Branded<string, 'EndpointKey'>;
interface EndpointKeyData {
	__ENDPOINT_PATH?: string;
	body?: unknown;
	name: EndpointName;
	pathParams?: unknown;
	queryParams?: unknown;
}
/**
 * Get an SWR key for an API endpoint.
 *
 * Just using the endpoint URL as the key is not enough for all endpoints
 * since some have a body that changes while the URL stays the same. This
 * key contains all parameters that can make the request unique.
 */
export function getEndpointKey(input: EndpointInput) {
	const hasParams = is.object(input);
	const pathParams =
		hasParams && 'pathParams' in input ? input.pathParams : undefined;
	const queryParams =
		hasParams && 'queryParams' in input ? input.queryParams : undefined;
	const body = hasParams && 'body' in input ? input.body : undefined;
	const name = is.string(input) ? input : input.name;

	return JSON.stringify({
		__ENDPOINT_PATH: endpoints[name].path,
		name,
		pathParams,
		queryParams,
		body,
	}) as EndpointKey;
}

/**
 * Parse an SWR key for an API endpoint.
 *
 * @see getEndpointKey()
 */
export function parseEndpointKey(key: EndpointKey) {
	try {
		const obj = JSON.parse(key) as EndpointKeyData;
		if (obj.__ENDPOINT_PATH !== endpoints[obj.name].path) {
			return undefined;
		}
		delete obj.__ENDPOINT_PATH;
		return (Object.keys(obj).length === 1 ? obj.name : obj) as EndpointInput;
	} catch {
		return undefined;
	}
}

interface UseEndpointOptions {
	isActive?: boolean;
	isImmutable?: boolean;
	keepPreviousData?: boolean;
}

/** Use SWR with an API endpoint. */
export function useEndpoint<T extends EndpointInput>(
	input: T | null,
	options?: UseEndpointOptions,
) {
	const {
		isActive = true,
		isImmutable = true,
		keepPreviousData = false,
	} = options ?? {};

	const useSWRHook = isImmutable ? useSWRImmutable : useSWR;
	const { data, error, isLoading, isValidating, mutate } = useSWRHook(
		isActive && input ? getEndpointKey(input) : null,
		(key) => {
			const parsedKey = parseEndpointKey(key) as T | undefined;
			if (!parsedKey) {
				throw new Error('Invalid endpoint key');
			}
			return fetchEndpointData(parsedKey);
		},
		{ errorRetryCount: 2, keepPreviousData },
	);

	const invalidateCache = useCallback(() => {
		ignorePromiseRejection(mutate());
	}, [mutate]);

	return {
		data,
		error: error as FetchError | undefined,
		isLoading,
		invalidateCache,
		isValidating,
	};
}
